import { useContext, useEffect, useState } from "react";

import { getAnalytics } from "@tm/insurance/analytics";
import { sendPolicyEmail } from "@tm/insurance/api";
import { navigationActions } from "@tm/insurance/navigation";
import { PASSPORT_DETAILS_PAGE_ID } from "@tm/isomorphic-utils";
import { getOrderDetails } from "@tm/server-api-utils";
import {
  AddAlternateContact,
  AddPassportDetail,
  EditTravellerSheet,
  FormattedText,
  H4Tag,
  ImageComponent,
  LandingButton,
  LandingButtonWhite,
  MoreBenefits,
  OrderDetailInsurance,
  OrderDetailPlan,
  OrderDetailTraveller,
  OrderGetHelp,
  OrderInsurance,
  ParaTag,
  PrimaryContact,
  SpanTag,
  Toast,
  TravAssistant,
} from "@tm/static";

import {
  initializePageTracking,
  trackEvent,
  triggerPageExit,
} from "@tm/insurance/analytics/xdm";
import {
  getDeviceIdForClient,
  getPartnerOnClientSide,
} from "@tm/insurance/utils";
import { ExperimentTrackingString, useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import {
  OrderDetailContent,
  OrderDetailLeft,
  OrderDetailMobileHd,
  OrderDetailRight,
  OrderDetailStyle,
  OrderDetailWrap,
} from "./OrderDetail.style";
import { IOrderDetails, OrderDetailsPageProps } from "./types";

const analytics = getAnalytics();

const OrderDetail = ({ data }: OrderDetailsPageProps) => {
  const device = useBreakpointV2();
  const [orderData, setOrderData] = useState<IOrderDetails>(data);
  const [addAlternateContact, setHandleAlternateContact] = useState(false);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [emailPolicyText, setEmailPolicyText] = useState("");
  const [editDetailSheet, setEditDetailSheet] = useState(false);
  const handleClickEditDetailSheet = () => {
    setEditDetailSheet(!editDetailSheet);
  };

  const {
    title,
    bookingId,
    orderDetailsData,
    downloadPolicy,
    addPassportDetailData,
    editTraveller,
    isPassportDetailsAvailable,
    travelAssistance,
    isExpired,
  } = orderData || {};

  const {
    insPlanName,
    insuranceInfo,
    planType,
    planIcon,
    planDetails,
    orderDetails,
    totalBenefits,
    travellersInsured,
    primaryContact,
    orderGethelp,
    forInsuranceContainer,
  } = orderDetailsData || {};

  const planData: any = {
    moreBenefits: planDetails?.moreBenefits,
    packName: planDetails?.planType,
    insuranceType: planDetails?.planName,
    insPlanName: planDetails?.planBenefitsSubHeader?.insurerName,
    vendorDisplayPrefix: planDetails?.planBenefitsSubHeader?.insurerInfo,
    ctaLink: planDetails?.TnC?.tncText,
    planExtraInfo: planDetails?.claimSettled,
    service: planDetails?.planBenefitsInfo,
    tncLink: planDetails?.TnC?.tncLink,
  };

  const experiment = useContext(ExperimentTrackingString) || "";

  useEffect(() => {
    analytics?.trackPageLoadEvent({ pageName: "ORDER_DETAILS", experiment });
    initializePageTracking({
      page_context: {
        page_name: "thankyou",
        page_type: "funnel",
        funnel_step: "thankyou",
        subpage_name: "order_details",
      },
    });
  }, []);

  useEffect(() => {
    if (data) {
      setOrderData(data);
    }
  }, [data]);

  /* istanbul ignore next */
  const handleAlternateContact = () => {
    setHandleAlternateContact(!addAlternateContact);
  };

  const insuranceHandler = async (item: any) => {
    if (item?.link) {
      setEmailPolicyText("");
      if (item.sectionType === "CLAIM_INSURANCE") {
        analytics?.trackOrderDetailsEvents(
          {
            actionType: "claim_insurance_clicked",
          }
          //"click"
        );
        trackEvent({
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: "claim_insurance_clicked",
          },
        });
      } else if (item.sectionType === "RAISE_ISSUE") {
        analytics?.trackOrderDetailsEvents(
          {
            actionType: "raise_issue_clicked",
          }
          //"click"
        );
        trackEvent({
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: "raise_issue_clicked",
          },
        });
      }

      return window.open(item?.link, "_blank");
    } else {
      if (item?.sectionType === "EMAIL") {
        const partner = getPartnerOnClientSide();
        let toastMessage = await sendPolicyEmail({
          bookingId,
          partner,
        });
        setEmailPolicyText(toastMessage.data.Description);
        analytics?.trackOrderDetailsEvents(
          {
            actionType: "email_policy_clicked",
          }
          //"click"
        );
        trackEvent({
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: "email_policy_clicked",
          },
        });
        return "";
      } else return;
    }
  };

  const navigateToDownloadPolicy = () => {
    const queryParameters = new URLSearchParams(window.location.search);
    const bookingId = queryParameters.get("bookingId");
    const meta_lob = queryParameters.get("meta_lob");
    const data = {
      bookingId,
      ...(meta_lob === "corp" && { meta_lob }),
    };

    analytics?.trackOrderDetailsEvents(
      {
        actionType: "download_policy_clicked",
      }
      //"click"
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "download_policy_clicked",
      },
    });
    triggerPageExit();

    return navigationActions.navigateToDownloadPolicy(data);
  };

  const navigateToAddPassport = () => {
    analytics?.trackOrderDetailsEvents(
      {
        actionType: "add_passport_clicked",
      }
      //"click"
    );

    navigationActions.navigateToAddPassport({
      bookingId,
      pageId: PASSPORT_DETAILS_PAGE_ID.ORDER_CONFIRMED,
    });
  };
  /* istanbul ignore next */
  const handleViewBebefitsClick = () => {
    setShowBenefitsModal(true);
    analytics?.trackOrderDetailsEvents(
      {
        actionType: "view_benefits_clicked",
      }
      //"click"
    );
  };

  const updateOrderDetails = async () => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const bookingId = queryParams.get("bookingId");
      const partner = getPartnerOnClientSide();
      const dvid = getDeviceIdForClient();

      const reqData = {
        partner,
        bookingId: bookingId as string,
        cookie: document.cookie,
        dvid: dvid || "",
      };
      const response = await getOrderDetails(reqData);
      setOrderData(response?.data?.data);
    } catch (error) {
      console.log("Error", { error });
    }
  };

  return (
    <>
      <OrderDetailStyle id={"pageId"}>
        <OrderDetailWrap>
          {device == "lg" && (
            <H4Tag>
              <FormattedText value={title} />{" "}
              <SpanTag fontSize="large"> ({bookingId}) </SpanTag>
            </H4Tag>
          )}
          <OrderDetailContent>
            <OrderDetailLeft>
              {device == "sm" && (
                <OrderDetailMobileHd>
                  <ImageComponent
                    width="60px"
                    height="40px"
                    src="https://tripmoneycmsimgak.mmtcdn.com/img/trip_Secured_9a14d8e25c.svg"
                  />
                  <ParaTag fontSize="md" fontWeight="bold">
                    Your Trip Is Secured
                  </ParaTag>
                  <ParaTag fontSize="xs" color={theme.greyText7}>
                    Order Id ({bookingId})
                  </ParaTag>
                  {downloadPolicy?.buttonTitle && (
                    <LandingButton
                      customWidth="100%"
                      customTitle={downloadPolicy.buttonTitle}
                      onClick={navigateToDownloadPolicy}
                    />
                  )}
                  {!isPassportDetailsAvailable && (
                    <AddPassportDetail
                      data={addPassportDetailData}
                      handlePassportBtn={navigateToAddPassport}
                    />
                  )}
                </OrderDetailMobileHd>
              )}
              <OrderDetailInsurance
                insPlanName={insPlanName}
                insuranceInfo={insuranceInfo}
                insuranceType={planType}
                orderDetailIcon={planIcon}
                orderDetails={orderDetails}
                forInsuranceContainer={forInsuranceContainer}
                handleClickInsurance={insuranceHandler}
                // insuranceTypeColor={
                //     orderDetailsData.insuranceTypeColor
                // }
              />
              {/* TODO: add more benefits */}
              {planDetails.title && (
                <OrderDetailPlan
                  title={planDetails?.title}
                  orderDetailIcon={planDetails?.icon}
                  planBenefits={planDetails?.planBenefits}
                  // totalBenefits={totalBenefits}
                  handleMoreBenefits={handleViewBebefitsClick}
                  subTitle={planDetails?.subtitle}
                  benefitsCTA={planDetails?.benefitsCTA}
                />
              )}
              <OrderDetailTraveller
                title={travellersInsured?.title}
                orderDetailIcon={travellersInsured?.icon}
                travellerDetails={travellersInsured?.travellerDetails}
                editEnabled={!isExpired}
                editTraveller={editTraveller}
                updateOrderDetails={updateOrderDetails}
                handleClickEditDetailSheet={handleClickEditDetailSheet}
              />
              <PrimaryContact
                title={primaryContact?.title}
                orderDetailIcon={primaryContact?.icon}
                contactDetails={primaryContact?.contactDetails}
                handleAlternateContact={handleAlternateContact}
              />
            </OrderDetailLeft>
            <OrderDetailRight>
              {travelAssistance && (
                <TravAssistant
                  cardTitle={travelAssistance?.heading?.title}
                  cardInfo={travelAssistance?.heading?.info}
                  helpLineNo={travelAssistance?.body?.title}
                  cardIcon={travelAssistance?.body?.icons}
                />
              )}
              {!isPassportDetailsAvailable && device == "lg" && (
                <AddPassportDetail
                  data={addPassportDetailData}
                  handlePassportBtn={navigateToAddPassport}
                />
              )}
              {downloadPolicy?.buttonTitle && device == "lg" && (
                <LandingButtonWhite
                  btnLeftIcon={downloadPolicy.leftIconUrl}
                  btnRightIcon={downloadPolicy.rightIconUrl}
                  customTitle={downloadPolicy.buttonTitle}
                  customWidth="100%"
                  onClick={navigateToDownloadPolicy}
                />
              )}
              {forInsuranceContainer?.forInsurance?.length && device == "lg" ? (
                <OrderInsurance
                  title={forInsuranceContainer?.title}
                  forInsurance={forInsuranceContainer?.forInsurance}
                  handleClickInsurance={insuranceHandler}
                />
              ) : (
                <></>
              )}
              {/* <OrderPriceBreakup
                                title={
                                    orderDetailsData.priceBreakupContainer.title
                                }
                                priceBreakup={
                                    orderDetailsData.priceBreakupContainer
                                        .priceBreakup
                                }
                                totalPrice={orderDetailsData.totalPrice}
                                mmtServiceFee={
                                    orderDetailsData.priceBreakupContainer
                                        .mmtServiceFee
                                }
                            />
                            <OrderAmtPaid
                                title="Amount Paid"
                                info={orderDetailsData.totalPrice}
                                handleInvoice={() => {}}
                                amountPaid={
                                    orderDetailsData.amountToBePaid.amountPaid
                                }
                            /> */}
              {orderGethelp && (
                <OrderGetHelp
                  title={orderGethelp.title}
                  getHelp={orderGethelp.getHelp}
                />
              )}
            </OrderDetailRight>
          </OrderDetailContent>
        </OrderDetailWrap>
      </OrderDetailStyle>
      {addAlternateContact && device === "lg" && (
        <AddAlternateContact
          handleAlternateContact={handleAlternateContact}
          title="Add Alternative Contact"
          goBackTxt="Go Back"
          addContactTxt="Add Contact"
          handleAddContact={() => {}}
          handleGoBack={() => {}}
        />
      )}
      {showBenefitsModal && (
        <MoreBenefits
          handleClose={() => setShowBenefitsModal(false)}
          moreBenfitsTitle={totalBenefits}
          data={planData}
        />
      )}
      {emailPolicyText && (
        <Toast
          toastCloseIcon="https://tripmoneycmsimgak.mmtcdn.com/img/cross_grey_rounded_e24cbc15d7.svg"
          toastIcon="https://tripmoneycmsimgak.mmtcdn.com/img/green_Check_Img_1c5717e1ba.svg"
          planSelectedTxt={emailPolicyText}
          autoCloseDuration={5000}
          width="24px"
          height="24px"
          showToast={emailPolicyText != ""}
          handleClose={() => setEmailPolicyText("")}
        />
      )}
      {editDetailSheet && (
        <EditTravellerSheet
          travellerData={travellersInsured?.travellerDetails}
          handleClose={handleClickEditDetailSheet}
          editTraveller={editTraveller}
          updateOrderDetails={updateOrderDetails}
        />
      )}
    </>
  );
};

export default OrderDetail;
